import React, { useState } from 'react';
import {
    Card,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CardContent,
    Popover,
    List,
    ListItem,
    ListItemText,
    Modal,
    Grid
} from '@mui/material';

// Material UI Icons
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';

// Context
import useMeta from '../../../hooks/useMeta';
// import { useSelector } from 'react-redux';
// import { hasScreenPermission } from '../../../utils/rolePermissions';

// Services
import { chatWithGemini } from '../../../Services/GeminiUtils';
import { searchInventory } from '../../../Services/Service';

import InventoryDetailsDialog from '../InventoryDetailsDialog/InventoryDetailsDialog';

const AISearch = ({ onAddToProposal }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [input, setInput] = useState('');
    const [currentSearchParams, setCurrentSearchParams] = useState(null);
    const [searchResultData, setSearchResultData] = useState([]);
    const [error, setError] = useState(null);
    const { searchCenters, getMicromarketsForCity } = useMeta();
    // const userData = useSelector(state => state.user.data);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    // Check if user has permission to access AISearch
    // if (!hasScreenPermission('ai-search', userData?.role)) {
    //     return (
    //         <Box sx={{ p: 3, textAlign: 'center' }}>
    //             <Typography variant="h6" color="error">
    //                 You don't have permission to access this feature.
    //             </Typography>
    //         </Box>
    //     );
    // }

    const handleMenuClick = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const handleViewDetails = (row) => {
        setSelectedRow(row);
        setDetailsOpen(true);
        setAnchorEl(null);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedRow(null);
    };

    const handleAddToProposal = (isRecommended) => {
        if (onAddToProposal) {
            const centerWithRecommendedStatus = {
                ...selectedRow,
                isRecommended: isRecommended
            };
            onAddToProposal(centerWithRecommendedStatus);
        }
        handleMenuClose();
    };

    const handleDownloadCSV = (data) => {
        if (!data || data.length === 0) return;

        const headers = [
            'Operator',
            'Center Name',
            'Inventory',
            'Billable Seats',
            'Availability Date',
            'Price/Seat',
            'Micromarket',
            'Locality'
        ];

        const rows = data.map(row => [
            row.operatorName || '',
            row.centerName || '',
            row.inventory || '',
            row.billableSeats || '',
            row.availabilityDate || '',
            row.pricePerSeat || '',
            row.microMarket || '',
            row.locality || ''
        ]);

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Search Results');
        
        XLSX.writeFile(workbook, 'search_results.xlsx');
    };

    const handleAISearch = async (searchParams) => {
        try {
            // If we have existing params, merge them with new ones
            const mergedParams = currentSearchParams ? {
                ...currentSearchParams,
                ...searchParams
            } : searchParams;

            // Validate micromarket for the city
            const availableMicromarkets = getMicromarketsForCity(mergedParams.city);
            if (!availableMicromarkets.includes(mergedParams.micromarket)) {
                setMessages(prev => [...prev, {
                    sender: 'assistant',
                    text: `I notice that "${mergedParams.micromarket}" is not a valid micromarket in ${mergedParams.city}. 
Here are the available micromarkets:
${availableMicromarkets.map(m => `• ${m}`).join('\n')}

Please choose one of these micromarkets and let me know which one you prefer.`
                }]);
                return;
            }

            // Convert AI search params to form data format
            const formattedData = {
                city: mergedParams.city,
                micromarket: mergedParams.micromarket,
                seatsNeeded: mergedParams.seats,
                startDate: new Date().toISOString().split('T')[0],
                duration: 1,
                searchType: 'AI'
            };

            // Handle budget formats
            if (mergedParams.budgetFrom && mergedParams.budgetTo) {
                formattedData.budgetFrom = mergedParams.budgetFrom;
                formattedData.budgetTo = mergedParams.budgetTo;
            } else if (mergedParams.budget) {
                formattedData.budget = mergedParams.budget;
            }

            // Save the current search parameters
            setCurrentSearchParams(mergedParams);

            // Call searchInventory directly
            const results = await searchInventory(formattedData);
            
            // Update the messages with search result
            if (results.success) {
                const budgetDisplay = mergedParams.budgetFrom && mergedParams.budgetTo ? 
                    `₹${mergedParams.budgetFrom} - ₹${mergedParams.budgetTo}` :
                    mergedParams.budget ? `₹${mergedParams.budget}` : 'Not specified';

                const summary = `I found ${results.data.length} spaces matching your criteria.`;

                setMessages(prev => [
                    ...prev,
                    { sender: 'assistant', text: summary },
                    {
                        sender: 'assistant',
                        content: results.data,
                        type: 'search-results'
                    },
                    {
                        sender: 'assistant',
                        text: `Current search parameters:
• City: ${mergedParams.city}
• Micromarket: ${mergedParams.micromarket}
• Seats: ${mergedParams.seats}
• Budget: ${budgetDisplay}

You can refine your search by:
• Changing the micromarket (available: ${availableMicromarkets.slice(0, 3).join(', ')}${availableMicromarkets.length > 3 ? '...' : ''})
• Modifying the number of seats
• Adjusting your budget (e.g., "budget between 10000-15000" or "budget 12000")`
                    }
                ]);
                setSearchResultData(results.data || []);
            } else {
                const errorMessage = results.error || 'An error occurred while searching for spaces';
                setError(errorMessage);
                setMessages(prev => [...prev, {
                    sender: 'assistant',
                    text: 'Sorry, I encountered an error while searching for spaces. Please try again or use the manual search.'
                }]);
                setSearchResultData([]);
            }
        } catch (error) {
            console.error('Error performing AI search:', error);
            setError(error.message || 'An error occurred while searching for spaces');
            setMessages(prev => [...prev, {
                sender: 'assistant',
                text: 'Sorry, I encountered an error while searching for spaces. Please try again or use the manual search.'
            }]);
            setSearchResultData([]);
        }
    };

    const handleSendMessage = async () => {
        if (!input.trim()) return;

        setLoading(true);
        setMessages(prev => [...prev, { sender: 'user', text: input }]);
        setInput('');

        try {
            const response = await chatWithGemini(input, currentSearchParams);
            if (response.searchParams) {
                // Check if only micromarket was changed
                if (currentSearchParams && 
                    Object.keys(response.searchParams).length === 1 && 
                    response.searchParams.micromarket) {
                    // Use current parameters with new micromarket
                    await handleAISearch({
                        ...currentSearchParams,
                        micromarket: response.searchParams.micromarket
                    });
                } else {
                    // Process full search parameters
                    await handleAISearch(response.searchParams);
                }
            } else {
                setMessages(prev => [...prev, { 
                    sender: 'assistant', 
                    text: "I couldn't understand your request. Please try specifying what you'd like to change (micromarket, seats, or budget)." 
                }]);
            }
        } catch (error) {
            console.error('Error in chat:', error);
            setMessages(prev => [...prev, { 
                sender: 'assistant', 
                text: 'Sorry, I encountered an error. Please try again.' 
            }]);
        }
        setLoading(false);
    };

    return (
        <Card sx={{ 
            borderRadius: 1,
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
            m: 0,
            overflow: 'hidden' // Prevent outer card from scrolling
        }}>
            <CardContent sx={{ 
                p: 3, 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column',
                '&:last-child': { pb: 3 },
                overflow: 'hidden' // Prevent card content from scrolling
            }}>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2, flexShrink: 0 }}>
                    Describe the space you're looking for in natural language. For example:
                    "I need a space for 50 people in Chennai, Guindy area with a budget of 15000 per seat"
                </Typography>

                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0, // Important for nested flex scrolling
                    overflow: 'hidden' // Prevent box from scrolling
                }}>
                    {/* Chat Interface */}
                    <Paper 
                        elevation={1} 
                        sx={{ 
                            flexGrow: 1,
                            display: 'flex', 
                            flexDirection: 'column',
                            borderRadius: 2,
                            overflow: 'hidden', // Prevent paper from scrolling
                            minHeight: 0 // Important for nested flex scrolling
                        }}
                    >
                        {/* Messages Area */}
                        <Box sx={{ 
                            flexGrow: 1, 
                            overflowY: 'auto', // Enable vertical scrolling
                            p: 2,
                            pb: 20, // Add extra padding at bottom to prevent content from being hidden
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f1f1f1',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#888',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#555',
                                },
                            },
                        }}>
                            {messages.map((message, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                                        mb: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: message.type === 'search-results'? '100%' : '70%',
                                            p: 2,
                                            bgcolor: message.sender === 'user' ? 'primary.light' : 'grey.100',
                                            borderRadius: 2,
                                            position: 'relative',
                                        }}
                                    >
                                        {message.type === 'search-results' ? (
                                            <Paper 
                                                elevation={0} 
                                                sx={{ 
                                                    color: '#FFFFFF'
                                                }}
                                            >
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'flex-end',
                                                    mb: 1
                                                }}>
                                                    <Button
                                                        startIcon={<DownloadIcon />}
                                                        onClick={() => handleDownloadCSV(message.content)}
                                                        size="small"
                                                        // sx={{ color: 'inherit' }}
                                                    >
                                                        Download
                                                    </Button>
                                                </Box>
                                                <TableContainer 
                                                    sx={{ 
                                                        maxHeight: 300, 
                                                        overflow: 'auto',
                                                        // bgcolor: '#565869',
                                                        borderRadius: 1,
                                                        '& .MuiTableCell-root': {
                                                            color: '#FFFFFF',
                                                            borderColor: '#666980'
                                                        }
                                                    }}
                                                >
                                                    <Table size="small">
                                                        <TableHead>
                                                        <TableRow sx={{ backgroundColor: (theme) => `${theme.palette.primary.main}08` }}>
                                                                <TableCell>Operator</TableCell>
                                                                <TableCell>Center Name</TableCell>
                                                                <TableCell>Inventory</TableCell>
                                                                <TableCell>Billable Seats</TableCell>
                                                                <TableCell>Price Per Seat</TableCell>
                                                                <TableCell>Micromarket</TableCell>
                                                                <TableCell>Locality</TableCell>
                                                                <TableCell>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {message.content.map((row, idx) => (
                                                                <TableRow key={idx}>
                                                                    <TableCell>{row.operatorName}</TableCell>
                                                                    <TableCell>{row.centerName}</TableCell>
                                                                    <TableCell>{row.inventory}</TableCell>
                                                                    <TableCell>{row.billableSeats}</TableCell>
                                                                    <TableCell>{row.pricePerSeat}</TableCell>
                                                                    <TableCell>{row.microMarket}</TableCell>
                                                                    <TableCell>{row.locality}</TableCell>
                                                                    <TableCell>
                                                                        <IconButton 
                                                                            size="small"
                                                                            onClick={(event) => handleMenuClick(event, row)}
                                                                        >
                                                                            <MoreVertIcon />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Paper>
                                        ) : (
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    color: message.sender === 'user' ? '#FFFFFF' : 'text.primary',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {message.text}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Floating Input Area */}
                        <Box 
                            sx={{ 
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: 2,
                                pb: 4,
                                // background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 20%, rgba(255,255,255,1) 100%)',
                                zIndex: 1000
                            }}
                        >
                            <Paper
                                elevation={3}
                                sx={{
                                    maxWidth: '800px',
                                    mx: 'auto',
                                    borderRadius: '24px',
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: '0 0 15px rgba(0,0,0,0.1)',
                                    bgcolor: 'background.paper'
                                }}
                            >
                                <Box sx={{ position: 'relative' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            position: 'absolute',
                                            top: -24,
                                            left: 16,
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Start your office search
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="standard"
                                        placeholder="Describe the space you're looking for..."
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                                        multiline
                                        maxRows={4}
                                        disabled={loading}
                                        InputProps={{
                                            disableUnderline: true,
                                            endAdornment: (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSendMessage}
                                                    disabled={loading}
                                                    sx={{
                                                        minWidth: 'unset',
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        mr: 1
                                                    }}
                                                >
                                                    {loading ? (
                                                        <CircularProgress size={20} color="inherit" />
                                                    ) : (
                                                        <SendIcon />
                                                    )}
                                                </Button>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                p: 2,
                                                alignItems: 'flex-end',
                                            },
                                            '& .MuiInputBase-input': {
                                                px: 2,
                                                py: 1,
                                            }
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>
                </Box>
            </CardContent>

            {/* Menu Popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <List sx={{ width: 250 }}>
                    <ListItem button onClick={() => handleViewDetails(selectedRow)}>
                        <ListItemText primary="View Details" />
                    </ListItem>
                    {/* Commenting out for now. Dont remove
                    <ListItem button onClick={() => handleAddToProposal(true)}>
                        <ListItemText primary="Add as Recommended Option" />
                    </ListItem>
                    <ListItem button onClick={() => handleAddToProposal(false)}>
                        <ListItemText primary="Add as Other Option" />
                    </ListItem>
                    <ListItem button onClick={handleDownloadCSV}>
                        <ListItemText primary="Download as CSV" />
                    </ListItem> */}
                </List>
            </Popover>

            {/* Inventory Details Dialog */}
            <InventoryDetailsDialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                center={selectedRow}
            />
        </Card>
    );
};

export default AISearch;
